import type { HeaderStat, WidgetDataContract } from "@nexus/widget-sdk";

/**
 * `grouped-metrics-table` v1 — a sortable table of per-row metrics, organized
 * into labeled column groups, with an optional expandable drill-down under
 * each row.
 *
 * Generic on purpose: nothing here names "protected event", "detection
 * engine" or any other domain concept — a source shapes its own column
 * groups, columns and row labels. The first consumer is the campaign report's
 * per-event KPI table (detection / response / impact groups, expanding to
 * engine-level rows), but the shape fits any "compare N entities across a
 * handful of grouped metrics, then drill into one" need. See "Reuse before
 * creating a widget" in `WIDGET_AUTHORING.md` before adding a sibling widget
 * for a case this could serve instead.
 */
export const GROUPED_METRICS_TABLE_CONTRACT: WidgetDataContract = {
  name: "grouped-metrics-table",
  version: 1,
};

export type MetricValueKind = "count" | "percent";

export interface MetricColumn {
  /** Matches a key in each row's `values` (and, when relevant, a drill-down sub-row's `values`). */
  key: string;
  label: string;
  kind: MetricValueKind;
}

export interface MetricColumnGroup {
  id: string;
  label: string;
  columns: MetricColumn[];
  /** Optional CSS color (a design token, e.g. `"var(--data-detection)"`) for the group heading. Omit for a neutral heading. */
  accentColor?: string;
}

/** One expandable drill-down block under a row, e.g. "Detection engines". */
export interface MetricSubRowGroup {
  label: string;
  /** Column keys this drill-down has values for; other columns render blank for these rows. */
  columnKeys: string[];
  rows: { id: string; label: string; values: Record<string, number | null> }[];
}

export interface MetricTableRow {
  id: string;
  label: string;
  /** Keyed by column key across every group. Missing or `null` renders as "–". */
  values: Record<string, number | null>;
  /** Omitted or empty: the row has no expand affordance. */
  drilldown?: MetricSubRowGroup[];
}

export interface GroupedMetricsTableFreshness {
  generatedAt: string;
  dataThrough: string;
  source: string;
  isEstimated: boolean;
}

export interface GroupedMetricsTableData {
  columnGroups: MetricColumnGroup[];
  rows: MetricTableRow[];
  freshness?: GroupedMetricsTableFreshness | null;
  /** Optional headline figures, shown in the header when the instance is collapsible. */
  summary?: HeaderStat[];
}
